import { useRef } from 'react';
import { scaleOrdinal } from 'd3-scale';
import type {
  ChoicesData,
  ChoicesStaticOptions,
  ChoicesVisualization,
} from '../types';
import { getLongestLabel } from '../utils/get-longest-label';
import { DotsGrid } from './dots-grid';
import { Option } from './option';
import { getScoreDisplayText } from './score/getScoreDisplayText';
import { MAX_NUMBER_OF_DOTS_WITHOUT_LAG } from './constants';

export const Dots = ({
  width,
  height,
  colors,
  data,
  staticOptions,
  disableAnimations,
  keyAccessor = (d: ChoicesData | ChoicesStaticOptions) => d.id.toString(),
  labelAccessor = (d: ChoicesStaticOptions) => d.label,
  valueAccessor = (d: ChoicesData) => d.score,
  isScoreHidden,
  fontFamily,
  textColor,
  shouldShowCorrect,
  shouldShowScorePercentage,
}: ChoicesVisualization & {
  isScoreHidden: boolean;
}) => {
  const labelsContainerRef = useRef<HTMLDivElement | null>(null);
  const rootHeight = Math.max(height, 0);
  const rootWidth = Math.max(width, 0);

  const discreteValuesBasedOnId = staticOptions.map(keyAccessor);

  const fillColors = scaleOrdinal(colors).domain(discreteValuesBasedOnId);

  if (rootWidth <= 0 || rootHeight <= 0) {
    return null;
  }

  const longestLabelId = getLongestLabel(
    staticOptions,
    labelAccessor,
    keyAccessor,
  );

  const largestOptionScore = Math.max(...data.map(valueAccessor), 0);

  return (
    <DotsGrid
      width={rootWidth}
      height={rootHeight}
      aria-hidden
      ref={labelsContainerRef}
      style={{ opacity: 0 }}
      largestNumberOfDots={Math.min(
        largestOptionScore,
        MAX_NUMBER_OF_DOTS_WITHOUT_LAG,
      )}
      data-testid="dots-visualization"
    >
      {staticOptions.map((option, i) => {
        const dataValue = data.find((d) => d.id === option.id);
        const isLongestLabel = longestLabelId === keyAccessor(option);
        const score = dataValue ? valueAccessor(dataValue) : 0;
        const cappedScore = Math.min(score, MAX_NUMBER_OF_DOTS_WITHOUT_LAG);

        return (
          <Option
            key={keyAccessor(option)}
            width={0}
            height={0}
            label={labelAccessor(option)}
            color={fillColors(keyAccessor(option))}
            scores={cappedScore}
            scoreDisplayText={getScoreDisplayText(
              score,
              shouldShowScorePercentage,
            )}
            shouldRenderTextResizer={
              isLongestLabel ? { labelsContainerRef } : false
            }
            isCorrect={option.isMarkedCorrect}
            fontFamily={fontFamily}
            textColor={textColor}
            shouldShowCorrect={shouldShowCorrect}
            disableAnimations={disableAnimations}
            shouldHideScores={isScoreHidden}
          />
        );
      })}
    </DotsGrid>
  );
};
